import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './VehiculoModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';

const PAGE_SIZE = 10;
const nombreTabla = "Vehículos";
const endpoint = ENDPOINTS.vehiculo;
const endpointById = ENDPOINTS.vehiculoById;
const modelo = Models.vehiculo;

// 👉 catálogo de tipos de vehículo
const endpointTipoVehiculo = ENDPOINTS.catTipoVehiculo;
const modeloTipoVehiculo = Models.tipoVehiculo;

const maps = {
  mapCreate: (f) => ({
    idTipoVehiculo: Number(f.idTipoVehiculo) || 0,
    placa: f.placa,
    economico: 'n/a',
    marca: f.marca,
    modelo: f.modelo,
    anio: Number(f.anio) || 0,
    color: f.color,
    capacidad: Number(f.capacidad) || 0,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idVehiculo: id,
    idTipoVehiculo: Number(f.idTipoVehiculo) || 0,
    placa: f.placa,
    economico: 'n/a',
    marca: f.marca,
    modelo: f.modelo,
    anio: Number(f.anio) || 0,
    color: f.color,
    capacidad: Number(f.capacidad) || 0,
    activo: !!f.activo,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [tipos, setTipos] = useState([]);            // 👈 catálogo de tipos
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 🚗 Vehículos + catálogo de tipos en paralelo
      const [resVeh, resTipos] = await Promise.all([
        axios.get(endpoint),
        axios.get(endpointTipoVehiculo),
      ]);

      setRows((resVeh.data || []).map((x) => modelo(x)));
      setTipos((resTipos.data || []).map((x) => modeloTipoVehiculo(x)));
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // 🔎 lookup idTipoVehiculo → etiqueta amigable
  const tipoLookup = useMemo(() => {
    const map = new Map();
    tipos.forEach((t) => {
      const id =
        t.idTipoVehiculo ??
        t.id ??
        t.value;
      const label =
        (t.clave && String(t.clave).trim()) ||
        t.nombre ||
        t.descripcion ||
        `Tipo #${id}`;
      if (id != null) {
        map.set(Number(id), label);
      }
    });
    return map;
  }, [tipos]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const placa = (r.placa || '').toLowerCase();
      const marca = (r.marca || '').toLowerCase();
      const modeloTxt = (r.modelo || '').toLowerCase();
      const color = (r.color || '').toLowerCase();

      // usamos nombre del tipo si existe
      const tipoLabel = (tipoLookup.get(r.idTipoVehiculo) || '').toLowerCase();
      const tipoId = String(r.idTipoVehiculo ?? '').toLowerCase();

      const anio = String(r.anio ?? '').toLowerCase();
      const cap = String(r.capacidad ?? '').toLowerCase();

      return (
        placa.includes(q) ||
        marca.includes(q) ||
        modeloTxt.includes(q) ||
        color.includes(q) ||
        tipoLabel.includes(q) ||  // 👈 búsqueda por nombre de tipo
        tipoId.includes(q) ||      // (seguimos permitiendo por id)
        anio.includes(q) ||
        cap.includes(q)
      );
    });
  }, [rows, query, tipoLookup]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idVehiculo === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idVehiculo ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por placa, económico, marca, modelo, color, tipo, año o capacidad…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Placa</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>Año</th>
                  <th>Color</th>
                  <th>Capacidad</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const tipoName = tipoLookup.get(r.idTipoVehiculo);
                  return (
                    <tr key={r.idVehiculo}>
                      {/* 👇 aquí mostramos el nombre del tipo */}
                      <td>{normalizeValue(tipoName ?? r.idTipoVehiculo)}</td>
                      <td><strong>{normalizeValue(r.placa)}</strong></td>
                      <td>{normalizeValue(r.marca)}</td>
                      <td>{normalizeValue(r.modelo)}</td>
                      <td>{normalizeValue(r.anio)}</td>
                      <td>{normalizeValue(r.color)}</td>
                      <td>{normalizeValue(r.capacidad)}</td>

                      {/* 🔽 Botón EDITAR con ícono */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idVehiculo)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>

                      {/* 🔽 Botón BORRAR con ícono */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            setEditingRowId(null);
                            const name = r.placa || `Vehículo ${r.idVehiculo}`;
                            askDelete(r.idVehiculo, name);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && paged.length === 0 && (<tr><td colSpan={11}>Sin resultados.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
